/**
 * Root React error boundary. Catches any render/lifecycle throw in the
 * whole tree, ships it to the backend watchdog, and shows a friendly
 * "something went wrong" fallback with a Reload + Report button.
 *
 * Wrap `<App />` in this at the top of the tree. One boundary is
 * enough — nested boundaries can be added around risky lazy pages
 * later if we want per-page recovery instead of a full-app fallback.
 */
import React from "react";
import { reportClientError } from "../lib/watchdog";
import axios from "axios";
import pkg from "../../package.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A crash whose message names a missing identifier (or a failed dynamic
// import / HTML-for-JS response) is the signature of a STALE or MISMATCHED
// bundle after a deploy — e.g. "Can't find variable: HighFiveTile" when an
// old shell loads a component that only exists in the newer build. These are
// NOT real logic bugs; the cure is to drop the cache and reload onto the
// current build. (Ordinary null/undefined bugs are deliberately excluded so
// we never mask a genuine defect behind an endless reload.)
const STALE_BUNDLE_RE = /can't find variable|is not defined|ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed|Unexpected token '<'/i;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", eventId: 0, sending: false, sent: false, stack: "", recovering: false };
  }

  static getDerivedStateFromError(error) {
    const message = (error && error.message) || "Unexpected error";
    return {
      hasError: true,
      // When the crash looks like a stale bundle, render a calm "Updating…"
      // screen instead of the scary error UI while we self-heal.
      recovering: STALE_BUNDLE_RE.test(message),
      message,
      eventId: Date.now(),
      stack: (error && error.stack) || "",
    };
  }

  componentDidCatch(error, info) {
    try {
      reportClientError({
        message: (error && error.message) || "React boundary caught error",
        stack: (error && error.stack) || "",
        componentStack: info?.componentStack || "",
        kind: "boundary",
      });
    } catch {
      /* ignore */
    }
    // Self-heal a stale/mismatched-bundle crash: bust caches + drop the SW
    // and reload onto the current build. Loop-guarded inside the helper so a
    // genuine crash reloads at most once, then falls through to the error UI.
    const msg = (error && error.message) || "";
    if (STALE_BUNDLE_RE.test(msg) && typeof window !== "undefined" && window.__wowForceFreshReload) {
      try { window.__wowForceFreshReload(); } catch { /* ignore */ }
    }
  }

  handleReload = () => {
    // Bust the service-worker cache on a manual reload too — a plain
    // location.reload() re-served the SAME broken cached bundle, which is why
    // a crash used to need a full app force-quit to clear.
    if (typeof window !== "undefined" && window.__wowForceFreshReload) {
      try { window.__wowForceFreshReload({ force: true }); return; } catch { /* fall through */ }
    }
    try { window.location.reload(); } catch { /* ignore */ }
  };

  handleSendToDev = async () => {
    if (this.state.sending || this.state.sent) return;
    this.setState({ sending: true });
    try {
      let crewId = "";
      try {
        const raw = sessionStorage.getItem("ecDashCrew") || localStorage.getItem("ecDashCrew");
        if (raw) {
          const parsed = JSON.parse(raw);
          crewId = parsed?.crew_id || parsed?.id || "";
        }
      } catch { /* ignore */ }
      await axios.post(`${API}/watchdog/dev-report`, {
        message: this.state.message.slice(0, 480),
        stack: (this.state.stack || "").slice(0, 8000),
        url: (typeof window !== "undefined" ? window.location.href : "").slice(0, 500),
        user_agent: (navigator.userAgent || "").slice(0, 400),
        crew_id: crewId,
        note: "User tapped 'Send to my developer' from crash screen",
        build_version: pkg.version || "",
      });
      this.setState({ sent: true, sending: false });
    } catch {
      this.setState({ sending: false });
    }
  };

  render() {
    if (this.state.recovering) {
      return (
        <div
          data-testid="app-error-boundary-updating"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background: "#0b1220",
            color: "#e2e8f0",
            fontFamily: "system-ui, -apple-system, sans-serif",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 34, height: 34, borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.2)", borderTopColor: "#fff",
              animation: "wowEbSpin 0.8s linear infinite", marginBottom: 16,
            }}
          />
          <p style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Updating to the latest version…</p>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0 0" }}>This only takes a moment.</p>
          <style>{`@keyframes wowEbSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#fafafa",
          color: "#0f172a",
          fontFamily: "system-ui, -apple-system, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 8px" }}>Something went sideways.</h1>
        <p style={{ maxWidth: 420, color: "#475569", margin: "0 0 20px", fontSize: 14 }}>
          The app hit an unexpected error. It&apos;s already been reported to your admin — you can safely reload.
        </p>
        <p style={{ maxWidth: 420, color: "#94a3b8", margin: "0 0 20px", fontSize: 12, fontFamily: "ui-monospace, monospace" }}>
          {this.state.message}
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            data-testid="app-error-boundary-reload"
            onClick={this.handleReload}
            style={{
              padding: "10px 22px",
              borderRadius: 999,
              border: "none",
              background: "#0f172a",
              color: "white",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
          <button
            data-testid="app-error-boundary-send-dev"
            onClick={this.handleSendToDev}
            disabled={this.state.sending || this.state.sent}
            style={{
              padding: "10px 22px",
              borderRadius: 999,
              border: "1px solid #cbd5e1",
              background: this.state.sent ? "#dcfce7" : "white",
              color: this.state.sent ? "#166534" : "#0f172a",
              fontSize: 14,
              fontWeight: 600,
              cursor: this.state.sending || this.state.sent ? "default" : "pointer",
              opacity: this.state.sending ? 0.6 : 1,
            }}
          >
            {this.state.sent ? "✓ Sent to dev" : this.state.sending ? "Sending…" : "Send to my developer"}
          </button>
        </div>
      </div>
    );
  }
}
